import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UserCog, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";

const emptyOp = { name: "", email: "", password: "", role: "operator", cambio_point_id: "", status: "active" };
const emptyCp = { name: "", address: "", phone: "", status: "active" };

export default function Config() {
  const { user } = useAuth();
  const [tab, setTab] = useState("operatori");
  const [ops, setOps] = useState(null);
  const [cps, setCps] = useState(null);
  const [opForm, setOpForm] = useState(emptyOp);
  const [opOpen, setOpOpen] = useState(false);
  const [cpForm, setCpForm] = useState(emptyCp);
  const [cpOpen, setCpOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/operators").then((r) => setOps(r.data));
    api.get("/cambio-points").then((r) => setCps(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveOp = async () => {
    try { await api.post("/operators", { ...opForm, cambio_point_id: opForm.cambio_point_id || null }); toast.success("Operatore creato"); setOpOpen(false); setOpForm(emptyOp); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCp = async () => {
    try { await api.post("/cambio-points", cpForm); toast.success("Cambio Point creato"); setCpOpen(false); setCpForm(emptyCp); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Configurazione" subtitle="Operatori e Cambio Point del Partner" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList><TabsTrigger value="operatori" data-testid="tab-operatori">Operatori</TabsTrigger><TabsTrigger value="cp" data-testid="tab-config-cp">Cambio Point</TabsTrigger></TabsList>
      </Tabs>

      {tab === "operatori" && (!ops ? <Loading /> : (
        <div>
          <div className="flex justify-end mb-3">
            <Dialog open={opOpen} onOpenChange={setOpOpen}>
              <DialogTrigger asChild><Button className="bg-[#0B2447]" data-testid="add-operator-btn"><Plus size={16} className="mr-1" />Nuovo operatore</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo operatore</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nome</Label><Input data-testid="op-name" className="mt-1" value={opForm.name} onChange={(e) => setOpForm({ ...opForm, name: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="op-email" className="mt-1" value={opForm.email} onChange={(e) => setOpForm({ ...opForm, email: e.target.value })} /></div>
                  <div><Label>Password</Label><Input data-testid="op-password" className="mt-1" value={opForm.password} onChange={(e) => setOpForm({ ...opForm, password: e.target.value })} placeholder="default: cambio123" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Ruolo</Label>
                      <Select value={opForm.role} onValueChange={(v) => setOpForm({ ...opForm, role: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="operator">Operatore</SelectItem><SelectItem value="partner_admin">Partner Admin</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Cambio Point</Label>
                      <Select value={opForm.cambio_point_id} onValueChange={(v) => setOpForm({ ...opForm, cambio_point_id: v })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{cps?.map((cp) => <SelectItem key={cp.id} value={cp.id}>{cp.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={saveOp} data-testid="save-operator" className="bg-[#0B2447]">Salva</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {ops.length === 0 ? <EmptyState icon={UserCog} title="Nessun operatore" /> : (
            <Card className="divide-y">
              {ops.map((o) => (
                <div key={o.id} className="flex items-center justify-between px-4 py-3" data-testid={`operator-${o.email}`}>
                  <div><p className="font-semibold text-[#0B2447]">{o.name}</p><p className="text-xs text-slate-400">{o.email} · {ROLE_LABELS[o.role]}</p></div>
                  <StatusBadge status={o.status} />
                </div>
              ))}
            </Card>
          )}
        </div>
      ))}

      {tab === "cp" && (!cps ? <Loading /> : (
        <div>
          <div className="flex justify-end mb-3">
            <Dialog open={cpOpen} onOpenChange={setCpOpen}>
              <DialogTrigger asChild><Button className="bg-[#0B2447]" data-testid="add-config-cp-btn"><Plus size={16} className="mr-1" />Nuovo Cambio Point</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo Cambio Point</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nome</Label><Input data-testid="config-cp-name" className="mt-1" value={cpForm.name} onChange={(e) => setCpForm({ ...cpForm, name: e.target.value })} /></div>
                  <div><Label>Indirizzo</Label><Input className="mt-1" value={cpForm.address} onChange={(e) => setCpForm({ ...cpForm, address: e.target.value })} /></div>
                  <div><Label>Telefono</Label><Input className="mt-1" value={cpForm.phone} onChange={(e) => setCpForm({ ...cpForm, phone: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={saveCp} className="bg-[#0B2447]">Salva</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cps.map((cp) => (
              <Card key={cp.id} className="p-5">
                <div className="flex items-center gap-3"><span className="p-2.5 rounded-xl bg-[#E4F5FB] text-[#0EA5C4]"><MapPin size={20} /></span>
                  <div><p className="font-display font-bold text-[#0B2447]">{cp.name}</p><p className="text-xs text-slate-400">{cp.code}</p></div>
                </div>
                <p className="text-sm text-slate-500 mt-3">{cp.address}</p>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
